import _ from "lodash";
import api from "../../../lib/axios";
import type { BaseResponse } from "../../../lib/api-contract";

/** Shared normalized option shape used by public option consumers. */
export interface PublicOptionResponse {
  label: string;
  value: string;
  id?: string;
  name?: string;
  type?: string;
  category?: string;
  icon?: string | null;
  icon_url?: string | null;
  icon_hover_url?: string;
}

export interface MerchantBrandItemResponse {
  id: string;
  merchant_brand_id: string;
  name: string;
  reward_name: string;
  points: number;
  value: number;
}

export interface MerchantBrandItemOption extends PublicOptionResponse {
  merchant_brand_id: string;
  reward_name: string;
  points: number;
  faceValue: number;
}

export interface NeighborhoodItemResponse {
  id: string;
  name: string;
  population?: number;
  latitude?: number;
  longitude?: number;
  order?: number;
  zone_type?: string;
  organization_id?: string;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
}

export interface AlertTypeListResponse {
  id: string;
  name: string;
  type: string;
  icon_url?: string | null;
  icon_hover_url?: string;
}

export type PublicOption = PublicOptionResponse;
export type NeighborhoodsApiResponse = BaseResponse<NeighborhoodItemResponse[]>;
export type AlertTypesApiResponse = BaseResponse<AlertTypeListResponse[]>;
export type PublicOptionsApiResponse = BaseResponse<
  Array<Omit<PublicOptionResponse, "label" | "value">>
>;

const toPublicOptionResponse = (
  item: Pick<PublicOptionResponse, "id" | "name" | "type" | "icon_url" | "icon_hover_url">,
): PublicOptionResponse => ({
  ...item,
  label: item.name ?? "",
  value: item.id ?? "",
});

export async function getPublicNeighborhoodOptions(
  organizationId: string,
  signal?: AbortSignal,
): Promise<PublicOptionResponse[]> {
  try {
    const response = await api.get<NeighborhoodsApiResponse>("/v1/auth/neighborhoods", {
      params: { organization_id: organizationId },
      signal,
    });

    if (response.data?.data) {
      return _.map(response.data!.data, (item): PublicOptionResponse =>
        toPublicOptionResponse({
          id: item.id,
          icon_hover_url: "",
          icon_url: null,
          name: item.name,
          type: "neighborgoo",
        }),
      );
    }

    throw new Error("Invalid API response structure");
  } catch (error) {
    if (!signal?.aborted) {
      console.error("Error fetching public neighborhood options:", error);
    }
    throw error;
  }
}

export async function getPublicCategoryOptions(
  signal?: AbortSignal,
): Promise<PublicOptionResponse[]> {
  try {
    const response = await api.get<PublicOptionsApiResponse>("/v1/category/event", {
      signal,
    });

    if (response.data?.data) {
      return _.map(response.data!.data, (item): PublicOptionResponse =>
        toPublicOptionResponse({
          id: item.id,
          icon_hover_url: item.icon_hover_url ?? "",
          icon_url: item.icon_url ?? null,
          name: item.name,
          type: item.type ?? "category",
        }),
      );
    }

    throw new Error("Invalid API response structure");
  } catch (error) {
    if (!signal?.aborted) {
      console.error("Error fetching public category options:", error);
    }
    throw error;
  }
}

export async function getPublicInterestOptions(
  signal?: AbortSignal,
): Promise<PublicOptionResponse[]> {
  try {
    const response = await api.get<PublicOptionsApiResponse>("/v1/category/interest", { signal });

    if (response.data?.data) {
      return _.map(response.data!.data, (item): PublicOptionResponse =>
        toPublicOptionResponse({
          id: item.id,
          icon_hover_url: "",
          icon_url: null,
          name: item.name,
          type: "interest",
        }),
      );
    }

    throw new Error("Invalid API response structure");
  } catch (error) {
    if (!signal?.aborted) {
      console.error("Error fetching public interest options:", error);
    }
    throw error;
  }
}

export async function getPublicMerchantBrandOptions(
  signal?: AbortSignal,
): Promise<PublicOptionResponse[]> {
  try {
    const response = await api.get<PublicOptionsApiResponse>("/v1/portal/category/brand", {
      signal,
    });

    if (response.data?.data) {
      return _.map(response.data!.data, (item): PublicOptionResponse =>
        toPublicOptionResponse({
          id: item.id,
          icon_hover_url: "",
          icon_url: null,
          name: item.name,
          type: "merchant_brand",
        }),
      );
    }

    throw new Error("Invalid API response structure");
  } catch (error) {
    if (!signal?.aborted) {
      console.error("Error fetching public merchant brand options:", error);
    }
    throw error;
  }
}

export async function getPublicMerchantBrandItemOptions(
  merchantBrandId: string,
  signal?: AbortSignal,
): Promise<MerchantBrandItemOption[]> {
  try {
    const response = await api.get<BaseResponse<MerchantBrandItemResponse[]>>(
      `/v1/portal/category/brand/${merchantBrandId}`,
      { signal },
    );

    if (response.data?.data) {
      return response.data.data.map((item) => ({
        id: item.id,
        merchant_brand_id: item.merchant_brand_id,
        name: item.name,
        reward_name: item.reward_name,
        points: item.points,
        faceValue: item.value,
        label: item.name,
        value: item.id,
        icon_hover_url: "",
        icon_url: null,
        type: "",
      }));
    }

    throw new Error("Invalid API response structure");
  } catch (error) {
    if (!signal?.aborted) {
      console.error("Error fetching public merchant brand item options:", error);
    }
    throw error;
  }
}

export async function getPublicAlertTypeOptions(
  signal?: AbortSignal,
): Promise<PublicOptionResponse[]> {
  try {
    const response = await api.get<AlertTypesApiResponse>("/v1/portal/category/alert-types", {
      signal,
    });

    if (response.data?.data) {
      return response.data.data
        .filter((item) => Boolean(item?.id && item?.name))
        .map((item): PublicOptionResponse => ({
          id: item.id,
          name: item.name,
          type: item.type,
          category: item.type,
          icon: item.icon_url ?? null,
          icon_url: item.icon_url ?? null,
          icon_hover_url: item.icon_hover_url ?? "",
          label: item.name,
          value: item.id,
        }));
    }

    throw new Error("Invalid API response structure");
  } catch (error) {
    if (!signal?.aborted) {
      console.error("Error fetching public alert type options:", error);
    }
    throw error;
  }
}
